import * as React from 'react';

import type { NormSetPeer } from '@graphql/queries/PerceptionModelQueries';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

/**
 * The members, named.
 *
 * Read only on purpose. This is the looking component: the builder keeps a
 * separate editing path, and a checkbox here would re-merge the two intents
 * that split exists to separate.
 *
 * Distance is a column but not a headline. Per row it is a legitimate ordering
 * — the list arrives sorted by it — while as a summary of the whole set it
 * says nothing about what the set tastes like.
 */

type Props = {
  members: NormSetPeer[];
  maxHeight?: number;
};

const MemberTable: React.FC<Props> = ({ members, maxHeight = 420 }) => {
  // Written by buildNormSet when distanceToAnchors could not score a product,
  // which happens to picks made from the map. Rendering null as 0 would put an
  // unmeasured product at the top of a list sorted by closeness.
  const unplaced = members.filter((m) => m.mean_dist === null).length;

  if (members.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        No products in this norm set yet.
      </Typography>
    );
  }

  return (
    <Box>
      <Box sx={{ maxHeight, overflowY: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell width={48}>#</TableCell>
              <TableCell>Product</TableCell>
              <TableCell align="right">Mean dist</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {members.map((m, i) => (
              <TableRow key={m.product} hover>
                <TableCell sx={{ color: 'text.secondary' }}>{i + 1}</TableCell>
                <TableCell>{m.product}</TableCell>
                <TableCell
                  align="right"
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {m.mean_dist === null ? (
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary' }}
                    >
                      not measured
                    </Typography>
                  ) : (
                    m.mean_dist.toFixed(3)
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {unplaced > 0 && (
        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 1, color: 'text.secondary' }}
        >
          {unplaced} product{unplaced === 1 ? '' : 's'} could not be measured
          against the anchors, so {unplaced === 1 ? 'it sits' : 'they sit'} at
          the end rather than at zero.
        </Typography>
      )}
    </Box>
  );
};

export default MemberTable;
